import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {findSortReactionInQuery, getQuery} from '@github-ui/list-view-items-issues-prs/Query'
import {getDefaultValues} from '@github-ui/relay-query-cache/helpers'
import {repoSidebarLinks} from '@github-ui/repos-issues-sidebar/repos-issues-sidebar-links'
import {useCallback} from 'react'
import type {Environment} from 'react-relay'
import {fetchQuery, useRelayEnvironment} from 'react-relay'

import type {IssueIndexPageQuery} from '../pages/__generated__/IssueIndexPageQuery.graphql'
import IssueIndexPageQueryNode from '../pages/__generated__/IssueIndexPageQuery.graphql'

export function buildIssueIndexVariables(
  rawQuery: string,
  repo: {owner: string; name: string},
  environment?: Environment,
): Record<string, unknown> {
  const bestMatchSortEnabled =
    (environment?.options as {bestMatchSortEnabled?: boolean} | undefined)?.bestMatchSortEnabled ?? false

  const variables: Record<string, unknown> = {
    query: getQuery(rawQuery, repo, bestMatchSortEnabled),
    owner: repo.owner,
    name: repo.name,
    includeReactions: findSortReactionInQuery(rawQuery),
    skip: 0,
  }

  if (isFeatureEnabled('issues_search_type_gql')) {
    variables.type = 'ISSUE_HYBRID'
  }

  return variables
}

export function usePreheatSidebarQueries(owner: string | undefined, repo: string | undefined) {
  const environment = useRelayEnvironment()

  const preheatAll = useCallback(() => {
    if (!isFeatureEnabled('issue_pinned_views_preheat_sidebar_links')) return
    if (!isLoggedIn()) return
    if (!owner || !repo) return

    for (const link of repoSidebarLinks) {
      const variables = buildIssueIndexVariables(link.query, {owner, name: repo}, environment)
      fetchQuery<IssueIndexPageQuery>(
        environment,
        IssueIndexPageQueryNode,
        variables as IssueIndexPageQuery['variables'],
        {
          networkCacheConfig: {
            metadata: {
              ttl: 1000 * 60 * 60, // 1 hour
              defaultValues: getDefaultValues(IssueIndexPageQueryNode),
              preheatSource: 'sidebar-idle',
              exclude_from_cache_key: ['includeReactions'],
            },
          },
        },
      ).subscribe({})
    }
  }, [environment, owner, repo])

  return {preheatAll}
}
